import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { ListOrderedIcon } from "lucide-react";
import { observer } from "mobx-react-lite";

import type { TrajectoryExpectationMode, TrajectoryMatchMode } from "../../../criterionConfig";
import { MultipleScenarios } from "../Inputs/MultipleScenarios";
import { ScorecardSourceSelector } from "../Inputs/ScorecardSourceSelector";
import { Field } from "../Layout/Field";
import { collectToolNamesFromColumn } from "../utils/extractTrajectoryTools";

import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { Toggle } from "@/components/Toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useSmartTableStore } from "@/routes/smart-table/smart-table-context";

interface TrajectoryFieldsProps {
  expectationMode: TrajectoryExpectationMode;
  onExpectationModeChange: (mode: TrajectoryExpectationMode) => void;
  expectedScenarios: string[][];
  onExpectedScenariosChange: (scenarios: string[][]) => void;
  noToolsExpected: boolean;
  onNoToolsExpectedChange: (enabled: boolean) => void;
  expectedSourceColumnId: string;
  onExpectedSourceColumnChange: (columnId: string) => void;
  sourceColumnOptions: { id: string; title: string }[];
  matchMode: TrajectoryMatchMode;
  onMatchModeChange: (mode: TrajectoryMatchMode) => void;
  hasInvalidExpectation: boolean;
  traceSourceColumnId: string;
}

export const TrajectoryFields = observer(
  ({
    expectationMode,
    onExpectationModeChange,
    expectedScenarios,
    onExpectedScenariosChange,
    noToolsExpected,
    onNoToolsExpectedChange,
    expectedSourceColumnId,
    onExpectedSourceColumnChange,
    sourceColumnOptions,
    matchMode,
    onMatchModeChange,
    hasInvalidExpectation,
    traceSourceColumnId,
  }: TrajectoryFieldsProps) => {
    const smartTableStore = useSmartTableStore();
    const sheetId = smartTableStore.activeTabId;
    const sheetDataStore = sheetId ? smartTableStore.getSheetDataStore(sheetId) : null;
    const toolSuggestions = collectToolNamesFromColumn(sheetDataStore, traceSourceColumnId);
    const isImportMode = expectationMode === "column";
    const hasTraceSource = Boolean(traceSourceColumnId);

    return (
      <div className="space-y-3">
        <Field label="Match mode">
          <Select
            value={matchMode}
            onValueChange={value => onMatchModeChange(value as TrajectoryMatchMode)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a mode..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="strict">Strict (exact sequence)</SelectItem>
              <SelectItem value="non_strict">Non-strict (subsequence)</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <SectionGroup
          title="Scenario"
          description="Define expected tool call sequences"
          icon={<ListOrderedIcon className="h-4 w-4" />}
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-700">Import from Column</span>
              <BasicTooltip content="Enable to import expected tools from a dataset column instead of listing them directly">
                <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
              </BasicTooltip>
            </div>
            <Toggle
              enabled={isImportMode}
              setEnabled={enabled => onExpectationModeChange(enabled ? "column" : "tools")}
            />
          </div>

          {!isImportMode ? (
            <div className="mb-4 flex items-center justify-between border-t border-gray-100 pt-4">
              <div className="flex items-center gap-1">
                <span className="text-sm text-gray-700">No tools should be called</span>
                <BasicTooltip content="Pass only when the agent trace contains no tool calls">
                  <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400" />
                </BasicTooltip>
              </div>
              <Toggle enabled={noToolsExpected} setEnabled={onNoToolsExpectedChange} />
            </div>
          ) : null}

          {isImportMode && (
            <div className="space-y-4">
              <div className="text-xs">
                Import from a dataset column. Cells should be JSON with{" "}
                <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px]">
                  accepted_scenarios
                </code>
                , each containing{" "}
                <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px]">
                  required_tools
                </code>
                . One scenario is enough for a single sequence.
              </div>
              <ScorecardSourceSelector
                value={expectedSourceColumnId}
                onValueChange={onExpectedSourceColumnChange}
                placeholder="Select a source..."
                options={sourceColumnOptions}
                name="Expected tools source"
              />
            </div>
          )}

          {!isImportMode && noToolsExpected && (
            <p className="text-xs text-gray-600">
              This scenario passes only when the Agent Trace contains no tool calls.
            </p>
          )}

          {!isImportMode && !noToolsExpected && (
            <div className="space-y-4">
              <div className="text-xs">
                Add tool sequences below. Pass if any scenario matches the Agent Trace (OR).
              </div>

              <MultipleScenarios
                scenarios={expectedScenarios}
                onScenariosChange={onExpectedScenariosChange}
                suggestions={toolSuggestions}
                hasTraceSource={hasTraceSource}
              />
            </div>
          )}

          {hasInvalidExpectation && (isImportMode || !noToolsExpected) ? (
            <p className="text-xs text-red-600">
              {isImportMode
                ? "Select an expected tools column."
                : "Add at least one expected tool in a scenario."}
            </p>
          ) : null}
        </SectionGroup>
      </div>
    );
  },
);
